import { faArrowsRotate, faGear, faLinkSlash } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import type { JSX } from 'react';
import React from 'react';
import { useTranslation } from 'react-i18next';

import type { MetaIntegration } from '@feathr/blackbox';
import { ConfirmModal, ContextMenu, toast } from '@feathr/components';
import { errorMessage, logError, useToggle } from '@feathr/hooks';

import { useMetaOAuth } from '../../PreConnection/MetaConnectButton/MetaConnectButton.hooks';

interface IMetaSettingsButtonProps {
  integration: MetaIntegration;
  onDisconnect: () => Promise<void>;
}

function MetaSettingsButton({
  integration,
  onDisconnect,
}: Readonly<IMetaSettingsButtonProps>): JSX.Element {
  const { t } = useTranslation();
  const [showModal, toggleModal] = useToggle(false);
  const { initiateOAuth, isLoading } = useMetaOAuth();

  async function handleDisconnect(): Promise<void> {
    try {
      await integration.disconnect();
    } catch (error) {
      const errorMsg = errorMessage(error, t);
      logError({
        message: 'Meta integration disconnect failed',
        level: 'error',
        extra: {
          error: errorMsg,
          integrationId: integration?.id,
        },
      });
      toast(
        t("Couldn't disconnect Meta. {{error}}", {
          error: errorMsg,
        }),
        { type: 'error' },
      );
      toggleModal();
      return;
    }

    toast(t('Disconnected Meta.'), { type: 'success' });
    toggleModal();
    await onDisconnect();
  }

  return (
    <>
      <ContextMenu
        buttonText={t('Settings')}
        buttonType={'secondary'}
        disabled={isLoading}
        name={'meta-settings'}
        suffix={<FontAwesomeIcon icon={faGear} />}
      >
        <ContextMenu.Item
          onClick={initiateOAuth}
          prefix={<FontAwesomeIcon icon={faArrowsRotate} />}
        >
          {t('Reconfigure')}
        </ContextMenu.Item>
        <ContextMenu.Divider />
        <ContextMenu.Item
          onClick={toggleModal}
          prefix={<FontAwesomeIcon icon={faLinkSlash} />}
          theme={'danger'}
        >
          {t('Disconnect')}
        </ContextMenu.Item>
      </ContextMenu>
      <ConfirmModal
        alertTitle={t('You will lose the ability to publish Meta campaigns.')}
        alertType={'danger'}
        confirmButtonText={t('Disconnect')}
        confirmButtonType={'danger'}
        onClose={toggleModal}
        onConfirm={handleDisconnect}
        opened={showModal}
        t={t}
        title={t('Disconnect Meta integration?')}
      />
    </>
  );
}

export default MetaSettingsButton;
